import './entity-licenses.system-administrator.screen.scss';
import {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import {TextChart} from '../../../../../../../components/charts/text-chart/text-chart';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {translations} from '../../../../../../../i18n/translations';
import {EntitiesRepository} from '../../../../../../../repositories/entities';
import {Box, Checkbox, Divider, FormControlLabel} from '@mui/material';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import {
  SubmitButton,
  SubmitButtonState,
} from '../../../../../../../components/submit-button/submit-button';
import {TextInput} from '../../../../../../../components/text-input/text-input';
import {LoadingContentAbsolute} from '../../../../../../../components/loading-content/loading-content-absolute';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {RouterPrompt} from '../../../../../../../components/router-prompt/router-prompt';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {PatchEntityContractAmendment} from '../../../../../../../backend/requests/patch-entity-contract-amendment';
import {Contract} from '../../../../../../../backend/interfaces/contract';
import {ContractAmendmentLogs} from '../../../../../../../components/history-logs/contract-ammendments-logs/_contract-amendments-logs';

export default function EntityLicensesSystemAdministratorOfficeScreen() {
  let {id}: {id?: string} = useParams();
  const {t} = useTranslation();
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Local state
  const [loading, setLoading] = useState<boolean>(true);
  const [licensesError, setLicensesError] = useState<string | undefined>();
  const [data, setData] = useState<PatchEntityContractAmendment | undefined>();
  const [origData, setOrigData] = useState<PatchEntityContractAmendment | undefined>();
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [submitButton, setSubmitButton] = useState({
    state: SubmitButtonState.Normal,
    disabled: true,
    dialogOpened: false,
  });
  const [contract, setContract] = useState<Contract | undefined>(undefined);

  // Get licenses data for the entity
  useEffect(() => {
    const init = async () => {
      try {
        // Reset state
        setSubmitButton((x) => ({
          ...x,
          state: SubmitButtonState.Normal,
        }));
        setDataChanged(false);

        // Start loading
        setLoading(true);

        // Check id
        if (!id) {
          // Error
          throw new Error('Empty entity id.');
        }

        // Make request to the backend
        const result = await EntitiesRepository.instance.getEntityContractsOverview(id);

        const data: PatchEntityContractAmendment = {
          // Note: The backend will return -1 when the number is undefined
          currentLicensesNumber: result.licensesNumber !== -1 ? result.licensesNumber : undefined,
          includesDigitalInclusion: result.includesDigitalInclusion,
          includesDigitalCareManagement: result.includesDigitalCareManagement,
        };

        const entityContract = result.activeContracts.find((x) => x.entityId === id);
        setContract(entityContract);
        setData(data);
        setOrigData(data);
      } catch (error) {
        // Log
        console.debug(
          `🔴 Could not get the number of monthly active licenses for entity ${id}.`,
          error
        );

        // Set value
        setData(undefined);
        setOrigData(undefined);
      } finally {
        // Stop loading
        setLoading(false);
      }
    };

    // Init
    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function checkDataWasChanged(newData: PatchEntityContractAmendment) {
    const changed = JSON.stringify(newData) !== JSON.stringify(origData);

    // Validate
    setDataChanged(changed);
    setSubmitButton((x) => ({
      ...x,
      disabled: !changed,
    }));

    // If licenses is below 0, disable the button (Invalid input)
    if (newData?.currentLicensesNumber && newData.currentLicensesNumber < 0) {
      setSubmitButton((x) => ({
        ...x,
        disabled: true,
      }));
    }
  }

  // Handle change monthly active licenses
  const handleLicensesChanges = (value: string | undefined) => {
    if (value) {
      // Parse value
      const parsed: number = parseInt(value, 10);

      const newData: PatchEntityContractAmendment = {
        ...data,
        currentLicensesNumber: isNaN(parsed) ? undefined : parsed,
      };
      setData(newData);
      checkDataWasChanged(newData);

      setLicensesError(
        parsed >= 0
          ? undefined
          : t(
              translations.entities.edit.licenses.errors
                .form_validation_invalid_number_of_monthly_active_licenses
            )
      );
    }
  };

  // Save changes
  const handleSaveChanges = (event: any) => {
    event.preventDefault();

    // Show confirm dialog
    setSubmitButton((x) => ({
      ...x,
      dialogOpened: true,
    }));
  };

  // Handle submit dialog opened state
  const handleSubmitDialogOpened = (opened: boolean) => {
    // Change dialog state
    setSubmitButton((x) => ({
      ...x,
      dialogOpened: opened,
    }));
  };

  // Submit changes
  const handleSubmit = async () => {
    try {
      // Update submit button state & close dialog
      setSubmitButton((x) => ({
        ...x,
        disabled: true, // lock button while loading
        state: SubmitButtonState.Loading,
        dialogOpened: false,
      }));

      // Check entity id
      if (!id) {
        throw new Error('Empty entity id.');
      }

      if (!data) {
        throw new Error('Empty data.');
      }

      // Compare data with origData and only update the changed fields.
      const dataKeys = Object.keys(data) as (keyof PatchEntityContractAmendment)[];
      const updatedData = dataKeys.reduce<PatchEntityContractAmendment>((acc, key) => {
        if (data[key] !== origData?.[key]) {
          // @ts-ignore
          acc[key] = data[key];
        }
        return acc;
      }, {});

      // Update the number of monthly active licenses
      const result = await EntitiesRepository.instance.updateEntityContractsOverview(
        id,
        updatedData
      );

      setOrigData({
        // Note: The backend will return -1 when the number is undefined
        currentLicensesNumber: result.licensesNumber !== -1 ? result.licensesNumber : undefined,
        includesDigitalInclusion: result.includesDigitalInclusion,
        includesDigitalCareManagement: result.includesDigitalCareManagement,
      });
      const entityContract = result.activeContracts.find((x) => x.entityId === id);
      setContract(entityContract);

      // Update submit button state
      setSubmitButton((x) => ({
        ...x,
        disabled: true,
        state: SubmitButtonState.Success,
      }));

      setDataChanged(false);
    } catch (error) {
      // Error
      console.debug('🔴 Could not edit the number of monthly active licenses.', error);

      // Set submit button state
      setSubmitButton((x) => ({
        ...x,
        disabled: false,
        state: SubmitButtonState.Error,
      }));
    }
  };

  return (
    <ContentPage>
      {!loading ? (
        <>
          <form onSubmit={handleSaveChanges}>
            <PageHeader
              rightButtons={
                <SubmitButton
                  disabled={submitButton.disabled}
                  state={submitButton.state}
                  onStateChange={(newState: SubmitButtonState) => {
                    setSubmitButton((x) => ({
                      ...x,
                      state: newState,
                    }));
                  }}
                  normalStateLabel={t(translations.entities.edit.licenses.save_button)}
                  loadingStateLabel={t(translations.entities.edit.licenses.saving_button)}
                  successStateLabel={t(
                    translations.entities.edit.licenses.success.save_success_button
                  )}
                  errorStateLabel={t(
                    translations.entities.edit.licenses.errors.error_on_save_button
                  )}
                  endIcon={<SaveIcon />}
                  type="submit"
                />
              }
            />
            <Box className="entity-licenses-system-administrator-container">
              <Box className="entity-licenses-system-administrator-text-chart-container">
                <TextChart
                  value={
                    data?.currentLicensesNumber === undefined
                      ? t(
                          translations.entities.edit.licenses
                            .number_of_monthly_active_licenses_not_defined
                        )
                      : data?.currentLicensesNumber
                  }
                  title={t(
                    translations.entities.edit.licenses.number_of_monthly_active_licenses_title
                  )}
                  loading={submitButton.state === SubmitButtonState.Loading}
                />
              </Box>
            </Box>
            <TextInput
              label={t(translations.entities.edit.licenses.number_of_monthly_active_licenses_title)}
              value={data?.currentLicensesNumber?.toString() ?? ''}
              handleChange={handleLicensesChanges}
              handleBlur={() => {
                if (data?.currentLicensesNumber === undefined) {
                  setLicensesError(
                    t(translations.entities.edit.licenses.errors.edit_form_validation_invalid_field)
                  );
                }
              }}
              error={licensesError}
              disabled={submitButton.state === SubmitButtonState.Loading}
              required={true}
              type="number"
              min="0"
            />

            <FormControlLabel
              label={t(translations.office.access.care_management)}
              control={
                <Checkbox
                  checked={data?.includesDigitalCareManagement}
                  disabled={submitButton.state === SubmitButtonState.Loading}
                  onChange={(e) => {
                    const val = e.target.checked;
                    const newData: PatchEntityContractAmendment = {
                      ...data,
                      includesDigitalCareManagement: val,
                    };
                    setData(newData);
                    checkDataWasChanged(newData);
                  }}
                />
              }
            />
            <br />

            <FormControlLabel
              label={t(translations.office.access.digital_inclusion)}
              control={
                <Checkbox
                  checked={data?.includesDigitalInclusion}
                  disabled={submitButton.state === SubmitButtonState.Loading}
                  onChange={(e) => {
                    const val = e.target.checked;
                    const newData: PatchEntityContractAmendment = {
                      ...data,
                      includesDigitalInclusion: val,
                    };
                    setData(newData);
                    checkDataWasChanged(newData);
                  }}
                />
              }
            />
          </form>

          <Divider sx={{my: 3}} />

          {/* History log */}
          <ContractAmendmentLogs contractAmendments={contract?.amendments} />

          {/* Save dialog */}
          <AlertModal
            id="confirm-save-number-monthly-active-licenses"
            opened={submitButton.dialogOpened}
            title={t(translations.entities.edit.licenses.edit_dialog_title)}
            content={t(translations.entities.edit.licenses.edit_dialog_content, {
              total: data?.currentLicensesNumber,
              entity: officeContext.state.entityForm?.name || '',
            })}
            handleClose={() => handleSubmitDialogOpened(false)}
            actions={[
              {
                label: t(translations.entities.edit.licenses.edit_dialog_action_cancel_button),
                type: 'neutral',
                handleClick: () => handleSubmitDialogOpened(false),
              },
              {
                label: t(translations.entities.edit.licenses.edit_dialog_action_save_button),
                type: 'nondestructive',
                handleClick: handleSubmit,
              },
            ]}
          />
          {/* Router */}
          <RouterPrompt
            when={dataChanged}
            subtitle={t(translations.entities.edit.licenses.router_prompt_title)}
            message={t(translations.entities.edit.licenses.router_prompt_message, {
              entity: officeContext.state.entityForm?.name || '',
            })}
            onOK={() => true}
            onCancel={() => false}
            toastNotification={{
              severity: 'info',
              message: t(
                translations.entities.edit.licenses
                  .number_of_monthly_active_licenses_edit_cancel_toast
              ),
            }}
          />
        </>
      ) : (
        <LoadingContentAbsolute />
      )}
    </ContentPage>
  );
}
